import type { WorkflowCompletion } from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import { useInnerWorkflow } from '@ovComponents/4-module/workflowCompletion/components/useInnerWorkflow';
import type { WorkflowCompletionWithId } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionWithId.types';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  type TransferContextData,
  toTransferContextData,
} from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { Box, Skeleton, Typography, useToast } from 'hephaestus/web';
import {
  type Dispatch,
  type SetStateAction,
  useContext,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';
import { DepositCard } from './depositCard';
import { resolveDepositReviewActions } from './depositReviewRules';

export type DepositReviewSubstepOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  enableMultipleDeposits?: boolean;
  enableDepositDelete?: boolean;
  enableDepositEdit?: boolean;
  enableAddAnotherDeposit?: boolean;
  depositEditWorkflow?: 'default' | string;
  createExternalDepositTransfer?: boolean;
  deferredExternalDepositTransfer?: boolean;
  showHoldings?: boolean;
  eftDisclaimer?: TranslatedString;
  internalTransferDisclaimer?: TranslatedString;
  externalTransferDisclaimer?: TranslatedString;
};

const DepositReviewVisual = ({
  options,
  dummyDeposit,
  setDummyDeposit,
  workflowCompletion,
  disableInteractions = false,
}: {
  options: DepositReviewSubstepOptions;
  dummyDeposit?: TransferContextData;
  setDummyDeposit?: Dispatch<SetStateAction<TransferContextData>>;
  workflowCompletion?: WorkflowCompletion;
  disableInteractions?: boolean;
}) => {
  const { applicableData, updateContextWithFields } = useDepositWorkflowData(
    dummyDeposit,
    setDummyDeposit,
    workflowCompletion,
  );

  const { canDelete, canEdit } = resolveDepositReviewActions(options);

  const { t } = useTranslation(['workflowCompletions']);
  const [loading] = useState(false);
  const { enqueueToast } = useToast();
  const { workflowData, userId } = useContext(WorkflowContext);
  const currentUserId = workflowData.currentUserId || userId;

  // Mock refetch function since we don't have data fetching in this component
  const refetch = async () => {
    // This would typically refetch data, but for deposits we manage state locally
  };

  const { createWorkflowCompletion, innerWorkflowModal } = useInnerWorkflow({
    refetch,
    workflowCompletion:
      workflowCompletion as unknown as WorkflowCompletionWithId,
  });

  const handleDepositDeleted = async (temporaryId: string) => {
    const updatedDeposits = temporaryDeposits.filter(
      (deposit) => deposit.temporaryId !== temporaryId,
    );
    await updateContextWithFields({
      temporaryDeposits: updatedDeposits,
      // If we're deleting the current deposit, clear the current deposit ID
      currentDepositTemporaryId:
        applicableData.currentDepositTemporaryId === temporaryId
          ? undefined
          : applicableData.currentDepositTemporaryId,
    });
  };

  const handleDepositEdit = async (temporaryId: string) => {
    if (
      !options.depositEditWorkflow ||
      options.depositEditWorkflow === 'default'
    ) {
      enqueueToast(t('depositReview.noWorkflowErrorMessage'), {
        variant: 'error',
      });
      return;
    }

    // Find the deposit to get its data for the workflow context
    const depositToEdit = temporaryDeposits.find(
      (deposit) => deposit.temporaryId === temporaryId,
    );

    if (!depositToEdit) {
      enqueueToast(t('depositReview.depositNotFoundError'), {
        variant: 'error',
      });
      return;
    }

    await createWorkflowCompletion({
      currentUserId,
      workflowId: options.depositEditWorkflow,
      workflowContext: {
        ...applicableData,
        ...depositToEdit,
        temporaryDeposits: [depositToEdit],
        currentDepositTemporaryId: temporaryId,
      },
      onWorkflowCompleted: async (completedContext) => {
        // Update the temporary deposit with the edited data from the inner workflow
        const updatedDeposits = temporaryDeposits.map((deposit) => {
          if (
            deposit.temporaryId !== temporaryId ||
            deposit.temporaryTransferState === 'completed'
          )
            return deposit;

          // Merge the completed context with the existing deposit, preserving the temporaryId
          // Handle all fields that might be explicitly set to undefined in the completed context
          const mergedData: typeof deposit = {
            ...deposit,
            ...toTransferContextData(completedContext),
            temporaryTransferState: undefined,
            temporaryId: deposit.temporaryId, // Ensure we keep the original temporaryId
          };
          return toTransferContextData(mergedData);
        });
        await updateContextWithFields({
          temporaryDeposits: updatedDeposits,
        });
      },
    });
  };

  const temporaryDeposits = applicableData.temporaryDeposits || [];

  return (
    <Box>
      {loading ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
        </>
      ) : temporaryDeposits.length > 0 ? (
        temporaryDeposits.map((deposit, index) => (
          <DepositCard
            key={deposit.temporaryId || index}
            deposit={deposit}
            index={index}
            onDepositDeleted={handleDepositDeleted}
            onDepositEdit={handleDepositEdit}
            enableDelete={canDelete && !disableInteractions}
            enableEdit={canEdit && !disableInteractions}
            defaultExpanded={!options.enableMultipleDeposits}
            options={options}
          />
        ))
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography
            variant='bodyMedium'
            sx={{ fontWeight: 500 }}
            color='textSecondary'
          >
            {t('depositReview.noDeposits')}
          </Typography>
        </Box>
      )}
      {innerWorkflowModal}
    </Box>
  );
};

export default DepositReviewVisual;
